import React, { useState, useEffect } from 'react';
import '../../questionsSearch/questionsSearch.scss';
import { NavLink, Outlet } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquarePlus, faSquareMinus } from '@fortawesome/pro-solid-svg-icons';
import { faCircleInfo } from '@fortawesome/pro-duotone-svg-icons';
import { useLazyGetPrimaryCourseListQuery } from '../../../features/authentication/questionSearchApiSlice';
import { useActivitySearchContext } from '../context';
import VocabularyResults from './components/vocabularyResults';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import ActivityTypes from '../../../enums/activityTypes';

const items = [
  {
    id: 1,
    name: 'Vocabulary',
    path: 'vocabularies',
    guideLines: <div className='guidelines'>
      <div className='d-flex flex-row align-items-center mb-2'>
        <FontAwesomeIcon color='#D5F2FF' style={{ fontSize: '35px', marginRight: '5px', '--fa-primary-color': '#63D0FF', '--fa-secondary-color': '#D5F2FF' }} icon={faCircleInfo} />
        <span className='fw-500'>Note that vocabularies are added and removed from flashcard upon clicking Add/Remove. To return to the flashcard, click Add Vocabularies.</span>
      </div>
      <ul>
        <li>Do not use any quotes in your search and please allow ample time for the page to refresh after typing keywords for the system to scan the database. The total number of vocabularies matching will update when finished.</li>
        <li>Enter ONE keyword in the search box to find vocabularies containing that word.</li>
        <li>Enter multiple keywords separated by OR to find vocabularies containing any of the keywords. The keywords do not have to be adjacent.
          <ul>
            <li>
              For example, inequality OR inequalities will find vocabularies with either of these keywords.
            </li>
          </ul>
        </li>
        <li>Enter multiple keywords separated by AND to find vocabularies containing all of the keywords. The keywords do not have to be adjacent.
          <ul>
            <li>
              For example, inequality AND inequalities will find vocabularies with both of these keywords.
            </li>
          </ul>
        </li>
        <li>Enter a specific phrase with words separated by spaces to find vocabularies containing that specific phrase. The keywords have to be adjacent.
          <ul>
            <li>
              For example, solve the equation will find vocabularies with that specific phrase.
            </li>
          </ul>
        </li>
      </ul>
    </div>
  },
  {
    id: 2,
    name: 'Exams/Flashcards',
    path: `exams_assignments/${ActivityTypes.FlashCard}`,
    guideLines: <div className='guidelines'>
      <div className='d-flex flex-row align-items-center mb-2'>
        <FontAwesomeIcon color='#D5F2FF' style={{ fontSize: '35px', marginRight: '5px', '--fa-primary-color': '#63D0FF', '--fa-secondary-color': '#D5F2FF' }} icon={faCircleInfo} />
        <span className='fw-500'>Note that vocabularies are added and removed from flashcards upon clicking Add/Remove. To return to the flashcards, click Add Vocabularies.</span>
      </div>
      <ul>
        <li>Use this tab when searching for keywords in titles of exams/flashcards.</li>
        <li>Do not use any quotes in your search and please allow ample time for the page to refresh after typing keywords for the system to scan the database. The total number of questions matching will update when finished.</li>
        <li>Enter ONE keyword in the search box to find exam/flashcard titles containing that word.</li>
        <li>Enter multiple keywords separated by OR to find exam/flashcard titles containing any of the keywords. The keywords do not have to be adjacent.
          <ul>
            <li>
              For example, inequality OR inequalities will find exam/flashcard titles with either of these keywords.
            </li>
          </ul>
        </li>
        <li>Enter multiple keywords separated by AND to find exam/flashcard containing all of the keywords. The keywords do not have to be adjacent.
          <ul>
            <li>
              For example, inequality AND inequalities will find exam/flashcard with both of these keywords.
            </li>
          </ul>
        </li>
        <li>Enter a specific phrase with words separated by spaces to find exam/flashcard containing that specific phrase. The keywords have to be adjacent.
          <ul>
            <li>
              For example, common core will find exam/flashcard with that specific phrase.
            </li>
          </ul>
        </li>
        <li className='fw-500'>For performance reasons the search is limited to the first 100 flashcards found. Use more keywords to narrow your search.</li>
      </ul>
    </div>
  },
  {
    id: 4,
    name: 'Vocabulary ID',
    path: 'vocabularyId',
    comingSoon: false,
    guideLines: <div className='guidelines'>

      <div className='d-flex flex-row align-items-center mb-2'>
        <FontAwesomeIcon color='#D5F2FF' style={{ fontSize: '35px', marginRight: '5px', '--fa-primary-color': '#63D0FF', '--fa-secondary-color': '#D5F2FF' }} icon={faCircleInfo} />
        <span className='fw-500'>Note that vocabularies are added and removed from flashcards upon clicking Add/Remove. To return to the flashcards, click Add Vocabularies.</span>
      </div>
      <ul>
        <li>Enter the EXACT number for the Vocabulary ID you wish to find. Do not use quotes or letters.</li>
        <li>If there is no vocabulary that matches the number you entered, a message will be displayed to let you know there was no match for that ID.</li>
      </ul>
    </div>
  }
];

const Index = () => {
  const activitySearchContext = useActivitySearchContext();
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [selectedItem, setSelectedItem] = useState(items[0]);
  const [getPrimaryCourses, {data: primaryCourseData}] = useLazyGetPrimaryCourseListQuery();

  useEffect(() => {
    try {
      handleGetCourseFilters();
    } catch (ex) {
      console.log(ex);
    }
  }, []);

  useEffect(() => {
    activitySearchContext.setQuestionsTabBasicCourse([]);
    activitySearchContext.setQuestionsTabBasicGradeIds([]);
  }, [selectedItem]);
  
  useEffect(() => {
    if (!primaryCourseData) return;
    activitySearchContext.setCourseLoading(false);
    const createSubCourseFilters = (courseId) => 
      primaryCourseData.courses.find(c => c.mainCourse.id === courseId)?.subCourses.map(c => ({ name: c.name, id: c.id, isChecked: false })) ?? [];

    var advMathFilters = createSubCourseFilters(101);
    var advScienceFilters = createSubCourseFilters(102);
    var advSocialStudiesFilters = createSubCourseFilters(103);
    var advELAFilters = createSubCourseFilters(104);
    var advForeignLanguageFilters = createSubCourseFilters(105);
    var advOtherFilters = createSubCourseFilters(0);
    var examTabGrades = activitySearchContext.gradeFilterOptions.map((item) => ({ ...item, isChecked: false }));
    var examTabCourses = primaryCourseData.courses.filter(c => c.mainCourse.id > 0).map(c => ({ name: c.mainCourse.name, id: c.mainCourse.id, isChecked: false }));
    var examTabStates = primaryCourseData.examStates.map(s => ({ name: s.name, id: s.id, isChecked: false }));
    var examTabYears = primaryCourseData.examYears.map(s => ({ name: s.name, id: s.id, isChecked: false }));

    activitySearchContext.setPrimaryCourseFilters(primaryCourseData.courses);
    activitySearchContext.setQuestionsTabAdvMathCourses(advMathFilters);
    activitySearchContext.setQuestionsTabAdvSocialStudiesCourses(advSocialStudiesFilters);
    activitySearchContext.setQuestionsTabAdvScienceCourses(advScienceFilters);
    activitySearchContext.setQuestionsTabAdvELACourses(advELAFilters);
    activitySearchContext.setQuestionsTabAdvForeignLanguageCourses(advForeignLanguageFilters);
    activitySearchContext.setQuestionsTabAdvOtherCourses(advOtherFilters);
    activitySearchContext.setExamsTabSelectedGrades(examTabGrades);
    activitySearchContext.setExamsTabSelectedCourses(examTabCourses);
    activitySearchContext.setExamsTabSelectedStates(examTabStates);
    activitySearchContext.setExamsTabSelectedExamYears(examTabYears);

  }, [primaryCourseData]);

  const handleGetCourseFilters = () => {
    activitySearchContext.setCourseLoading(true);
    getPrimaryCourses().unwrap;
  };

  const handleTabSwitcth = (item) => {
    setSelectedItem(item);
    activitySearchContext.setQuestionsTabBasicCourse([]);
    activitySearchContext.setQuestionsTabBasicGradeIds([]);
    activitySearchContext.setShowResults(false);
    activitySearchContext.setActivityName('');
  };
  
  return (
    <div className="d-flex flex-column">
      <div className="d-flex flex-column whiteCard">
        <div className='d-flex flex-row justify-content-between'>
          <div className='castleQuestionsList mb-4'>
            {items.map((item, index) =>
              item.comingSoon ? (
                <OverlayTrigger
                  key={`searchTypeToolTipKey${index}`}
                  placement="top"
                  overlay={
                    <Tooltip id={`toolTipId${index}`} className="custom-tooltip">
                      <p className="mb-0">Coming Soon!</p>
                    </Tooltip>
                  }>
                  <div
                    id={item.name}
                    key={`searchType${index}`}
                    className="castleQuestionTypeBtn disabled"
                  >
                    <div className="iconBox"></div>
                    {item.name}
                  </div>
                </OverlayTrigger>
               
              ) : (
                <NavLink
                  id={item.name}
                  key={`searchType${index}`}
                  className="castleQuestionTypeBtn"
                  to={item.path}
                  end
                  onClick={() => handleTabSwitcth(item)}
                >
                  <div className="iconBox"></div>
                  {item.name}
                </NavLink>
              )
            )}
          </div>

          <div className='d-flex flex-row align-items-center justify-content-center fw-500' style={{ color: '#09BCF3', fontSize: '14px', cursor: 'pointer' }} onClick={() => setShowGuidelines(!showGuidelines)} onBlur={() => setSelectedItem(items[0])}>
            <span>Guidelines</span>
            <FontAwesomeIcon style={{ fontSize: '18px', marginLeft: '5px' }} icon={showGuidelines ? faSquareMinus : faSquarePlus} />
          </div>
        </div>
        {showGuidelines && selectedItem.guideLines}
        <Outlet 
          context={{
            fetchCourseFilters: handleGetCourseFilters,
            courseFilters: primaryCourseData
          }}/>
      </div>
      <VocabularyResults/>
    </div>
  );
};

export default Index;
